import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  EquipmentSerial,
  EquipmentTypeSummary,
  EquipmentProduct,
  EquipmentTypeDraft,
  EquipmentTypeDraftInput,
} from '@/types/inventory'
import {
  listEquipmentTypes,
  fetchEquipmentSerials,
  listEquipmentProducts,
  listEquipmentDrafts,
  upsertEquipmentDraft,
  deleteEquipmentDraft,
} from '@/lib/inventoryApi'
import { createLogger } from '@/lib/logger'

const log = createLogger('InventoryStore')

export const useInventoryStore = defineStore('inventory', () => {
  const types = ref<EquipmentTypeSummary[]>([])
  const typesLoading = ref(false)
  const typesError = ref<string | null>(null)

  const products = ref<EquipmentProduct[]>([])
  const productsLoading = ref(false)
  const productsError = ref<string | null>(null)

  const drafts = ref<EquipmentTypeDraft[]>([])
  const draftsLoading = ref(false)
  const draftsError = ref<string | null>(null)

  const serialsMap = ref<Record<number, EquipmentSerial[]>>({})
  const serialsLoading = ref<Record<number, boolean>>({})
  const serialsError = ref<Record<number, string | null>>({})

  const onboardingStepKey = ref<'core' | 'specs' | 'assets' | 'accessories' | 'review'>('core')

  async function loadTypes() {
    typesLoading.value = true
    typesError.value = null
    try {
      types.value = await listEquipmentTypes()
    } catch (error) {
      log.error('loadTypes failed', error)
      typesError.value = error instanceof Error ? error.message : String(error)
    } finally {
      typesLoading.value = false
    }
  }

  async function loadSerials(equipmentTypeId: number, force = false) {
    if (!force && serialsMap.value[equipmentTypeId]) return
    serialsLoading.value = { ...serialsLoading.value, [equipmentTypeId]: true }
    serialsError.value = { ...serialsError.value, [equipmentTypeId]: null }
    try {
      const serials = await fetchEquipmentSerials(equipmentTypeId)
      serialsMap.value = { ...serialsMap.value, [equipmentTypeId]: serials }
    } catch (error) {
      log.error('loadSerials failed', error)
      serialsError.value = {
        ...serialsError.value,
        [equipmentTypeId]: error instanceof Error ? error.message : String(error),
      }
    } finally {
      serialsLoading.value = { ...serialsLoading.value, [equipmentTypeId]: false }
    }
  }

  async function loadProducts(force = false) {
    if (!force && products.value.length) return
    productsLoading.value = true
    productsError.value = null
    try {
      products.value = await listEquipmentProducts()
    } catch (error) {
      log.error('loadProducts failed', error)
      productsError.value = error instanceof Error ? error.message : String(error)
    } finally {
      productsLoading.value = false
    }
  }

  async function loadDrafts(force = false) {
    if (!force && drafts.value.length) return
    draftsLoading.value = true
    draftsError.value = null
    try {
      drafts.value = await listEquipmentDrafts()
    } catch (error) {
      log.error('loadDrafts failed', error)
      draftsError.value = error instanceof Error ? error.message : String(error)
    } finally {
      draftsLoading.value = false
    }
  }

  async function saveDraft(payload: EquipmentTypeDraftInput & { id?: number }) {
    const draft = await upsertEquipmentDraft({
      progressStep: onboardingStepKey.value,
      ...payload,
    })
    drafts.value = [draft, ...drafts.value.filter((item) => item.id !== draft.id)]
    return { id: draft.id }
  }

  async function removeDraft(id: number) {
    await deleteEquipmentDraft(id)
    drafts.value = drafts.value.filter((draft) => draft.id !== id)
  }

  function setOnboardingStep(key: 'core' | 'specs' | 'assets' | 'accessories' | 'review') {
    onboardingStepKey.value = key
  }

  const typeById = computed(() => Object.fromEntries(types.value.map((type) => [type.id, type])))

  function getSerials(equipmentTypeId: number) {
    return serialsMap.value[equipmentTypeId] ?? []
  }

  return {
    types,
    typesLoading,
    typesError,
    loadTypes,
    products,
    productsLoading,
    productsError,
    loadProducts,
    serialsMap,
    serialsLoading,
    serialsError,
    loadSerials,
    typeById,
    getSerials,
    drafts,
    draftsLoading,
    draftsError,
    loadDrafts,
    saveDraft,
    removeDraft,
    onboardingStepKey,
    setOnboardingStep,
  }
})
